import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { CommissionStatusType, ICommission, INewCommissionDTO } from '../../../models/commissions.models';
import * as commissionSelectors from '../../../store/agents.selectors';
import * as commissionActions from '../../../store/agents.actions';
import { distinctUntilChanged, filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { FetchBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchTags } from 'src/app/features/tags/store/tags.actions';
import { FetchCategories } from 'src/app/features/categories/store/categories.actions';
import { CommissionAgentsComponent } from './commission-agents/commission-agents.component';
import { CommissionThresholdsComponent } from './commission-thresholds/commission-thresholds.component';
import { CommissionNegotiationComponent } from './commission-negotiation/commission-negotiation.component';
import { CommissionSettingsComponent } from './commission-settings/commission-settings.component';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { CommissionComfirmationDialogComponent } from './commission-comfirmation-dialog/commission-comfirmation-dialog.component';
import { Actions, ofType } from '@ngrx/effects';
import { IAgentFilter } from '../../../models/agents.models';
import { parseAgentFilterRequest, refillAgentFilter } from 'src/app/shared/utils/helpers';

@Component({
  selector: 'app-commission-details',
  templateUrl: './commission-details.component.html',
  styleUrls: ['./commission-details.component.scss'],
})
export class CommissionDetailsComponent implements OnInit {
  @ViewChild('commissionSettings') commissionSettingsComponent!: CommissionSettingsComponent;
  @ViewChild('commissionAgents') commissionAgentsComponent!: CommissionAgentsComponent;
  @ViewChild('commissionThresholds') commissionThresholdsComponent!: CommissionThresholdsComponent;
  @ViewChild('commissionNegotiation') commissionNegotiationComponent!: CommissionNegotiationComponent;

  commissionId!: number;
  commission$: Observable<ICommission | null> = this.store.select(commissionSelectors.selectCommission);
  commission: ICommission | null = null;
  commissionDTO: Partial<INewCommissionDTO> = {};

  rightPanelActiveTab: 'agents' | 'thresholds' | 'negotiation' = 'agents';

  thresholdsTabValid: boolean = true;
  negotiationTabValid: boolean = true;

  destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private actions: Actions,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private commonService: CommonService,
    private translateService: TranslateService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.commissionId = +this.activatedRoute.snapshot.paramMap.get('id')!;
    this.store.dispatch(commissionActions.GetCommissionById({ id: this.commissionId }));
    this.store.dispatch(SetCurrentElementId({ currentElementId: this.commissionId }));

    let firstLoad = true;
    this.commission$
      .pipe(
        filter((commission) => commission != null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((commission: ICommission | null) => {
        if (commission) {
          this.commission = commission;

          if (firstLoad) {
            const filter:IAgentFilter = refillAgentFilter()
            if (this.commission.type === 'brand') {
              // Fetch all brands, labels and categories only on first load
              this.store.dispatch(FetchBrands({ page: { number: 0, size: 0 } }));
              this.store.dispatch(FetchTags({ page: { number: 0, size: 0 } }));
              this.store.dispatch(FetchCategories({ page: { number: 0, size: 0 } }));
            }
            else{
              filter.extraInfoFilter.has_base_commission = false; 
            }
            this.store.dispatch(commissionActions.GetAgents({filter: parseAgentFilterRequest(filter)}));
            firstLoad = false;
          }
        }
      });

    this.actions
      .pipe(
        ofType(commissionActions.DeleteCommissionsSuccess),
        tap(() => {
          this.router.navigate(['/agents/commissions']);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  goBack(): void {
    this.router.navigate(['/agents/commissions']);
  }

  updateCommission() {
    const detailsForm = this.commissionSettingsComponent?.detailsForm;
    const thresholdForm = this.commissionThresholdsComponent?.thresholdForm;
    const negotiationForm = this.commissionNegotiationComponent?.negotiationForm;

    const agents = this.commissionAgentsComponent?.agents;

    // Validate all forms
    const settingsValid = detailsForm?.valid;
    const thresholdsValid = thresholdForm?.valid;
    const negotiationValid = negotiationForm?.valid;

    // Mark all as touched to show errors
    detailsForm?.markAllAsTouched();
    thresholdForm?.markAllAsTouched();
    negotiationForm?.markAllAsTouched();

    this.thresholdsTabValid = thresholdsValid;
    this.negotiationTabValid = negotiationValid;

    if (!settingsValid || !thresholdsValid || !negotiationValid) {
      // Abort update, maybe show a message
      console.log('Some forms are invalid');
      this.commonService.showToast(this.translateService.instant('alerts.wizard.uncomplete_steps', { steps: ''}), 'WARNING', { duration: 3000 });
      return;
    }

    this.commissionDTO = {
      ...detailsForm.getRawValue(),
      ...thresholdForm.getRawValue(),
      ...negotiationForm.getRawValue(),
      agent_ids: agents.map((i) => i.id),
    };

    this.store.dispatch(commissionActions.UpdateCommission({ id: this.commissionId, commission: this.commissionDTO }));
  }

  toggleCommission() {
    const toggle: CommissionStatusType = this.commission?.status === 'active' ? 'disabled' : 'active';

    this.store.dispatch(commissionActions.UpdateCommission({ id: this.commissionId, commission: { status: toggle } }));
  }

  confirmationDialog(type: 'edit' | 'delete' | 'enable' | 'disable') {
    const dialogRef = this.dialog.open(CommissionComfirmationDialogComponent, {
      data: {
        dialogType: type,
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        if (type === 'edit') {
          this.updateCommission();
        }

        if (type === 'disable' || type === 'enable') {
          this.toggleCommission();
        }

        if (type === 'delete') {
          this.store.dispatch(commissionActions.DeleteCommissions({ ids: [this.commissionId] }));
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(commissionActions.ClearCommission());
  }
}
